import type { PropertyDetailListing } from '@/src/components/features/property/types';
import { getPublicListingName } from '@/src/lib/seo/listing-metadata';
import { getListingIconType, ICON_FOR_LISTING_TYPE } from '@/src/lib/utils/listingPropertyType';
import { compactNumber, summaryPriceText } from '../lib/listingUtils';

const RANGE_SEPARATOR = '–';

type FloorplanLike = {
  beds?: number;
  baths?: number;
  sqft?: number;
};

type AddressLike = {
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
  hideStreet?: boolean;
};

type MetricRange = {
  min: number;
  max: number;
};

type ModelSummaryLike = {
  minBeds?: number;
  maxBeds?: number;
  minBaths?: number;
  maxBaths?: number;
  minSqft?: number;
  maxSqft?: number;
};

type ModelLike = {
  numBeds?: number;
  baths?: number;
};

type ListingDetailsLike = {
  roomForRent?: {
    roomDetails?: { roomSqFt?: number | string };
    roommateDetails?: { roommatesCount?: string };
  };
};

export type ListingSummaryPropertyTypeTranslationKey =
  | 'apartment'
  | 'apartments'
  | 'condo'
  | 'duplex'
  | 'house'
  | 'land'
  | 'room'
  | 'roomForRent'
  | 'townhouse';

export type ListingSummaryPropertyTypeLabel =
  | { type: 'translation'; key: ListingSummaryPropertyTypeTranslationKey }
  | { type: 'fallback'; label: string };

function cleanString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function normalizeForComparison(value: string): string {
  return value.toLowerCase().replace(/\s+/g, ' ');
}

function includesStreetText(value: string, street: string): boolean {
  return Boolean(street && normalizeForComparison(value).includes(normalizeForComparison(street)));
}

function formatAddress({
  street,
  city,
  state,
  zip,
}: {
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
}): string {
  const cityStateZip = [cleanString(city), [cleanString(state), cleanString(zip)].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ');

  return [cleanString(street), cityStateZip].filter(Boolean).join(', ');
}

function normalizeWebsiteUrl(value: unknown): string {
  const url = cleanString(value);
  if (!url || url === '#') return '';
  if (/^(https?:)?\/\//i.test(url)) return url;
  if (/^[a-z0-9.-]+\.[a-z]{2,}(?:[/:?#]|$)/i.test(url)) return `https://${url}`;
  return '';
}

function getFirstCleanString(...values: Array<unknown>): string {
  return values.map(cleanString).find(Boolean) ?? '';
}

function getVisibleListingName(currentListing: PropertyDetailListing, street: string, hideStreet: boolean): string {
  const candidates = [
    currentListing.title,
    (currentListing as { name?: string }).name,
    currentListing.displayName,
    (currentListing as { propertyName?: string }).propertyName,
  ];

  for (const value of candidates) {
    const candidate = cleanString(value);
    if (candidate && (!hideStreet || !includesStreetText(candidate, street))) {
      return candidate;
    }
  }

  return '';
}

export function getListingSummaryTitle(currentListing: PropertyDetailListing): string {
  const address = currentListing.address as AddressLike | undefined;
  const hideStreet = Boolean(address?.hideStreet);
  const street = cleanString(address?.street);

  if (hideStreet) {
    return getPublicListingName({
      address,
      displayName: currentListing.displayName,
      title: currentListing.title,
    });
  }

  const visibleListingName = getVisibleListingName(currentListing, street, hideStreet);

  return visibleListingName || street;
}

function normalizeRangeText(value: unknown): string {
  return cleanString(value).replace(/\s*[-–]\s*/g, RANGE_SEPARATOR);
}

function normalizeSqftText(value: unknown): string {
  return normalizeRangeText(value).replace(/\bsq\.?\s*ft\.?\b/i, 'sqft');
}

function toMetricNumber(value: unknown, allowZero: boolean): number | undefined {
  const numberValue = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(numberValue)) return undefined;
  if (allowZero ? numberValue < 0 : numberValue <= 0) return undefined;
  return numberValue;
}

function normalizeMetricRange(minValue: unknown, maxValue: unknown, allowZero: boolean): MetricRange | null {
  const minNumber = toMetricNumber(minValue, allowZero);
  const maxNumber = toMetricNumber(maxValue, allowZero);
  if (minNumber == null && maxNumber == null) return null;

  const min = minNumber ?? maxNumber!;
  const max = maxNumber ?? minNumber!;
  return {
    min: Math.min(min, max),
    max: Math.max(min, max),
  };
}

function rangeFromValues(values: Array<unknown>, allowZero: boolean): MetricRange | null {
  const numbers = values
    .map((value) => toMetricNumber(value, allowZero))
    .filter((value): value is number => value != null);

  if (numbers.length === 0) return null;
  return {
    min: Math.min(...numbers),
    max: Math.max(...numbers),
  };
}

function formatCompactBedRange(range: MetricRange | null): string {
  if (!range) return '';
  if (range.min === 0) {
    if (range.max === 0) return 'Studio';
    return `Studio${RANGE_SEPARATOR}${compactNumber(range.max)} bd`;
  }
  if (range.min !== range.max) {
    return `${compactNumber(range.min)}${RANGE_SEPARATOR}${compactNumber(range.max)} bd`;
  }
  return `${compactNumber(range.min)} bd`;
}

function formatCompactBathRange(range: MetricRange | null): string {
  if (!range) return '';
  if (range.min !== range.max) {
    return `${compactNumber(range.min)}${RANGE_SEPARATOR}${compactNumber(range.max)} ba`;
  }
  return `${compactNumber(range.min)} ba`;
}

function formatSqftRange(range: MetricRange | null): string {
  if (!range) return '';
  if (range.min !== range.max) {
    return `${compactNumber(range.min)}${RANGE_SEPARATOR}${compactNumber(range.max)} sqft`;
  }
  return `${compactNumber(range.min)} sqft`;
}

const PROPERTY_TYPE_LABEL_KEYS: Record<string, ListingSummaryPropertyTypeTranslationKey> = {
  townhouse: 'townhouse',
  divided: 'duplex',
  duplex: 'duplex',
  condo: 'condo',
  house: 'house',
  land: 'land',
  room: 'room',
};

function getHaloPropertyTypeLabel(
  rawPropertyType: string | undefined,
  listingType: string | undefined,
  hasAnyRange: boolean,
): ListingSummaryPropertyTypeLabel | null {
  if (listingType === 'room') return { type: 'translation', key: 'roomForRent' };

  const rawLabel = cleanString(rawPropertyType);
  const propertyType = rawLabel.toLowerCase();
  if (!propertyType) return null;
  if (
    propertyType === 'large' ||
    propertyType === 'medium' ||
    propertyType === 'garden' ||
    propertyType === 'apartment'
  ) {
    return { type: 'translation', key: hasAnyRange ? 'apartments' : 'apartment' };
  }

  const key = PROPERTY_TYPE_LABEL_KEYS[propertyType];
  return key ? { type: 'translation', key } : { type: 'fallback', label: rawLabel };
}

export function getListingSummaryDisplay(currentListing: PropertyDetailListing) {
  const { active, isAllowedToRetain } = currentListing;
  const shouldHideDetails = active === false && isAllowedToRetain === false;

  const priceSummary = currentListing.pricing?.summary;
  const minMaxPriceBeds = currentListing.listingMinMaxPriceBeds;

  const priceObj =
    priceSummary?.priceLow || priceSummary?.priceHigh
      ? priceSummary
      : {
          priceLow: minMaxPriceBeds?.minPrice,
          priceHigh: minMaxPriceBeds?.maxPrice,
        };

  const priceDisplay = summaryPriceText(priceObj);
  const showPrice = priceDisplay && priceDisplay !== 'Call';

  // listingMinMaxPriceBeds is a building-level aggregate that can include bed configs
  // with no available units, causing a mismatch with the bedroom tabs in UnitsModule
  // (which are derived from floorplans). Prefer floorplans as the source of truth,
  // then pricing.summary (like legacy HDP), then listingMinMaxPriceBeds as last resort.
  const floorplans = currentListing.floorplans as Array<FloorplanLike> | undefined;
  const directMetrics = currentListing as {
    bedrooms?: number;
    beds?: number;
    numBeds?: number;
    bathrooms?: number;
    baths?: number;
    numBaths?: number;
    sqft?: number;
    sqFt?: number;
    squareFeet?: number;
  };
  const modelSummary = (currentListing as { modelSummary?: ModelSummaryLike }).modelSummary;
  const models = (currentListing as { models?: Array<ModelLike> }).models;

  const bedRange =
    rangeFromValues(floorplans?.map((fp) => fp.beds) ?? [], true) ||
    normalizeMetricRange(priceSummary?.bedsLow, priceSummary?.bedsHigh, true) ||
    normalizeMetricRange(directMetrics.bedrooms ?? directMetrics.beds ?? directMetrics.numBeds, undefined, true) ||
    normalizeMetricRange(minMaxPriceBeds?.minBeds, minMaxPriceBeds?.maxBeds, true) ||
    normalizeMetricRange(modelSummary?.minBeds, modelSummary?.maxBeds, true) ||
    rangeFromValues(models?.map((model) => model.numBeds) ?? [], true);
  const bedRangeDisplay =
    formatCompactBedRange(bedRange) ||
    normalizeRangeText(
      getFirstCleanString(
        minMaxPriceBeds?.bedDisplayCompact,
        minMaxPriceBeds?.bedDisplaySemiCompact,
        minMaxPriceBeds?.bedDisplay,
      ),
    );
  const showBedRange = bedRangeDisplay && bedRangeDisplay !== '';

  const bathRange =
    rangeFromValues(floorplans?.map((fp) => fp.baths) ?? [], false) ||
    normalizeMetricRange(priceSummary?.bathsLow, priceSummary?.bathsHigh, false) ||
    normalizeMetricRange(directMetrics.bathrooms ?? directMetrics.baths ?? directMetrics.numBaths, undefined, false) ||
    normalizeMetricRange(minMaxPriceBeds?.minBaths, minMaxPriceBeds?.maxBaths, false) ||
    normalizeMetricRange(modelSummary?.minBaths, modelSummary?.maxBaths, false) ||
    rangeFromValues(models?.map((model) => model.baths) ?? [], false);
  const bathRangeDisplay =
    formatCompactBathRange(bathRange) ||
    normalizeRangeText(
      getFirstCleanString(
        minMaxPriceBeds?.bathDisplayCompact,
        minMaxPriceBeds?.bathDisplaySemiCompact,
        minMaxPriceBeds?.bathDisplay,
      ),
    );
  const showBathRange = bathRangeDisplay && bathRangeDisplay !== '';

  const sqftRange =
    rangeFromValues(floorplans?.map((fp) => fp.sqft) ?? [], false) ||
    normalizeMetricRange(priceSummary?.sqFtLow, priceSummary?.sqFtHigh, false) ||
    normalizeMetricRange(directMetrics.sqft ?? directMetrics.sqFt ?? directMetrics.squareFeet, undefined, false) ||
    normalizeMetricRange(minMaxPriceBeds?.minSqft, minMaxPriceBeds?.maxSqft, false) ||
    normalizeMetricRange(modelSummary?.minSqft, modelSummary?.maxSqft, false);
  const sqftDisplay = formatSqftRange(sqftRange) || normalizeSqftText(minMaxPriceBeds?.sqftDisplay);
  const showSqft = sqftDisplay !== '';

  const floorplanCount = floorplans?.length ?? 0;
  const forRentUnitsInBuilding = currentListing.forRentUnitsInBuilding ?? [];
  const buildingHref = normalizeWebsiteUrl(currentListing.uriBuilding) ? '' : cleanString(currentListing.uriBuilding);
  const listingDetails = currentListing.details as ListingDetailsLike | undefined;
  const isRoomForRent = currentListing.listingType === 'room';
  const isSublet = currentListing.listingType === 'sublet';
  const propertyType = (currentListing as { propertyType?: string }).propertyType;
  const propertyTypeSlug = propertyType?.toLowerCase();
  const isApartment =
    propertyTypeSlug === 'large' ||
    propertyTypeSlug === 'medium' ||
    propertyTypeSlug === 'garden' ||
    propertyTypeSlug === 'apartment';
  const isCondo = propertyTypeSlug === 'condo';
  const isApartmentWithFloorplans = floorplanCount > 0 && isApartment;
  const legacyOtherUnitsCount = Math.max(forRentUnitsInBuilding.length - 1, 0);
  const otherUnitsCount = legacyOtherUnitsCount;
  const showUnits = !isRoomForRent && !isSublet && isApartmentWithFloorplans;
  const showOtherUnitsLink = isCondo && otherUnitsCount > 0 && Boolean(buildingHref);

  const roomForRent = listingDetails?.roomForRent;
  const roomSqFt = roomForRent?.roomDetails?.roomSqFt;
  const roommatesCount = roomForRent?.roommateDetails?.roommatesCount;
  const showRoomSqFt = isRoomForRent && Boolean(roomSqFt);
  const showHousemates = isRoomForRent && Boolean(roommatesCount);
  const housematesIsSingular = roommatesCount === '1';

  const address = currentListing.address as AddressLike | undefined;
  const hideStreet = Boolean(address?.hideStreet);
  const street = cleanString(address?.street);
  const buildingTitle = getListingSummaryTitle(currentListing);
  const websiteDisplayName =
    !hideStreet && buildingTitle && !includesStreetText(buildingTitle, street) ? buildingTitle : null;
  const addressDisplay = formatAddress({
    street: !hideStreet && !includesStreetText(buildingTitle, street) ? street : '',
    city: address?.city,
    state: address?.state,
    zip: address?.zip,
  });

  const websiteUrl = normalizeWebsiteUrl(currentListing.details?.webSite);

  // Plural awareness for the inlined property type — "apartments" if any
  // detail range is present, otherwise "apartment".
  const bedsAreRange = Boolean(bedRange && bedRange.min !== bedRange.max);
  const bathsAreRange = Boolean(bathRange && bathRange.min !== bathRange.max);
  const sqftIsRange = Boolean(sqftRange && sqftRange.min !== sqftRange.max);
  const hasAnyRange = bedsAreRange || bathsAreRange || sqftIsRange;

  const propertyTypeLabel = getHaloPropertyTypeLabel(propertyType, currentListing.listingType, hasAnyRange);
  const showPropertyType = propertyTypeLabel != null;

  const iconType = getListingIconType(
    (currentListing as { propertyType?: string }).propertyType,
    currentListing.listingType,
  );
  const AddressIcon = ICON_FOR_LISTING_TYPE[iconType];

  return {
    shouldHideDetails,
    priceDisplay,
    showPrice,
    bedRangeDisplay,
    showBedRange,
    bathRangeDisplay,
    showBathRange,
    sqftDisplay,
    showSqft,
    showUnits,
    otherUnitsCount,
    buildingHref,
    showOtherUnitsLink,
    roomSqFt,
    showRoomSqFt,
    roommatesCount,
    showHousemates,
    housematesIsSingular,
    addressDisplay,
    buildingTitle,
    websiteUrl,
    websiteDisplayName,
    propertyTypeLabel,
    showPropertyType,
    AddressIcon,
  };
}
